import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'

export default class extends Controller {
  static targets = [
    'loading',
    'dauCanvas', 'screenViewsCanvas', 'topScreensCanvas',
    'featureUsageCanvas', 'errorsCanvas',
    'dauValue', 'screenViewsValue', 'avgSessionValue', 'errorsValue',
    'totalSessionsValue', 'avgDurationValue', 'avgEventsValue'
  ]

  static values = {
    url: String,
    startDate: String,
    endDate: String
  }

  connect() {
    this.charts = {}
    this.isLoading = false
    this.loadData()
  }

  disconnect() {
    this.destroyAllCharts()
  }

  destroyAllCharts() {
    Object.keys(this.charts).forEach((name) => {
      if (this.charts[name]) {
        this.charts[name].destroy()
        this.charts[name] = null
      }
    })
    this.charts = {}
  }

  updateDateRange(event) {
    const { startDate, endDate } = event.detail
    this.startDateValue = startDate
    this.endDateValue = endDate
    this.loadData()
  }

  async loadData() {
    if (this.isLoading) return
    this.isLoading = true
    this.showLoading()

    try {
      const params = new URLSearchParams({
        start_date: this.startDateValue,
        end_date: this.endDateValue
      })

      const response = await fetch(`${this.urlValue}?${params}`)
      const data = await response.json()

      // Destroy existing charts before creating new ones
      this.destroyAllCharts()

      this.updateStats(data)
      this.updateCharts(data)
    } catch (error) {
      console.error('Failed to load analytics data:', error)
    } finally {
      this.isLoading = false
      this.hideLoading()
    }
  }

  updateStats(data) {
    // DAU - last day's value
    const dauData = data.daily_active_users?.data || []
    const latestDau = dauData[dauData.length - 1] || 0
    if (this.hasDauValueTarget) {
      this.dauValueTarget.textContent = this.formatNumber(latestDau)
    }

    // Screen views total
    const screenViewsTotal = (data.screen_views?.data || []).reduce((a, b) => a + b, 0)
    if (this.hasScreenViewsValueTarget) {
      this.screenViewsValueTarget.textContent = this.formatNumber(screenViewsTotal)
    }

    // Session stats
    const sessionStats = data.session_stats || {}
    if (this.hasAvgSessionValueTarget) {
      this.avgSessionValueTarget.textContent = this.formatDuration(sessionStats.avg_duration_seconds || 0)
    }
    if (this.hasTotalSessionsValueTarget) {
      this.totalSessionsValueTarget.textContent = this.formatNumber(sessionStats.total_sessions || 0)
    }
    if (this.hasAvgDurationValueTarget) {
      this.avgDurationValueTarget.textContent = this.formatDuration(sessionStats.avg_duration_seconds || 0)
    }
    if (this.hasAvgEventsValueTarget) {
      this.avgEventsValueTarget.textContent = (sessionStats.avg_events_per_session || 0).toFixed(1)
    }

    // Errors total
    const errorsTotal = (data.error_rates?.data || []).reduce((a, b) => a + b, 0)
    if (this.hasErrorsValueTarget) {
      this.errorsValueTarget.textContent = this.formatNumber(errorsTotal)
    }
  }

  updateCharts(data) {
    this.renderLineChart('dau', data.daily_active_users, 'Daily Active Users', 'accent')
    this.renderLineChart('screenViews', data.screen_views, 'Screen Views', 'success')
    this.renderLineChart('errors', data.error_rates, 'Errors', 'error')
    this.renderBarChart('topScreens', data.top_screens, 'Views')
    this.renderBarChart('featureUsage', data.feature_usage, 'Usage')
  }

  renderLineChart(name, chartData, label, color) {
    const canvas = this[`${name}CanvasTarget`]
    if (!canvas) return

    if (this.charts[name]) {
      this.charts[name].destroy()
    }

    const colors = {
      accent: { bg: 'rgba(79, 70, 229, 0.1)', border: 'rgb(79, 70, 229)' },
      success: { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgb(16, 185, 129)' },
      error: { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgb(239, 68, 68)' }
    }

    const colorSet = colors[color] || colors.accent

    this.charts[name] = new Chart(canvas, {
      type: 'line',
      data: {
        labels: chartData?.labels || [],
        datasets: [{
          label: label,
          data: chartData?.data || [],
          fill: true,
          backgroundColor: colorSet.bg,
          borderColor: colorSet.border,
          borderWidth: 2,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { maxTicksLimit: 7 }
          },
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(0,0,0,0.05)' }
          }
        }
      }
    })
  }

  renderBarChart(name, chartData, label) {
    const canvas = this[`${name}CanvasTarget`]
    if (!canvas) return

    if (this.charts[name]) {
      this.charts[name].destroy()
    }

    const labels = Object.keys(chartData || {}).slice(0, 10)
    const values = Object.values(chartData || {}).slice(0, 10)

    this.charts[name] = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: label,
          data: values,
          backgroundColor: 'rgba(79, 70, 229, 0.7)',
          borderColor: 'rgb(79, 70, 229)',
          borderWidth: 1
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { color: 'rgba(0,0,0,0.05)' }
          },
          y: {
            grid: { display: false }
          }
        }
      }
    })
  }

  async loadFunnel(event) {
    const funnelId = event.currentTarget.dataset.funnelId
    const funnelUrl = event.currentTarget.dataset.funnelUrl

    // For now, open in a modal or log - full implementation would show funnel details
    console.log('Loading funnel:', funnelId, funnelUrl)
  }

  formatNumber(num) {
    return new Intl.NumberFormat().format(num)
  }

  formatDuration(seconds) {
    if (!seconds || seconds === 0) return '0m'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins === 0) return `${secs}s`
    return `${mins}m ${secs}s`
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }
}
